import { Box, Sheet, Stack, Typography } from "@mui/joy";
import {
  TaskEditorBlockType,
  TaskEditorDescription,
} from "pages/task-editor/utils/taskEditorUtils";
import { theme } from "styles/theme";

// 미리보기에서 reference(automatic_thought) 에 표시할 샘플 텍스트
export const PREVIEW_SAMPLE_AUTOMATIC_THOUGHT = "나는 이 일을 망칠 것 같아.";

interface PreviewDescriptionProps {
  description: TaskEditorDescription;
  // 텍스트(content) 의 header/body 스타일은 block.type 이 단독으로 결정한다.
  blockType: TaskEditorBlockType;
}

function ContentText({
  content,
  blockType,
}: {
  content: string;
  blockType: TaskEditorBlockType;
}) {
  const isEmpty = !content;
  const text = content || "(텍스트 없음)";

  if (blockType === "header") {
    return (
      <Typography
        variant="H3"
        sx={{
          color: isEmpty
            ? theme.vars.palette.Content.Weak
            : theme.vars.palette.Content.Strong,
          fontWeight: 700,
          wordBreak: "keep-all",
        }}
      >
        {text}
      </Typography>
    );
  }

  // body — v1 TypographyCell 의 body 는 wordBreak 미지정(브라우저 기본 normal)
  // 이라 한글이 글자 단위로 줄을 꽉 채워 줄바꿈된다. 프리뷰도 동일하게 맞춘다
  // (keep-all 미적용). header 만 keep-all 로 단어 끊김을 막는다.
  return (
    <Typography
      variant="Body"
      sx={{
        color: isEmpty
          ? theme.vars.palette.Content.Weak
          : theme.vars.palette.Content.Strong,
        fontWeight: 500,
        whiteSpace: "pre-wrap",
        // 본문 행간을 테마 Body(132%)보다 넓혀 가독성↑ (미리보기 한정).
        lineHeight: 1.5,
      }}
    >
      {text}
    </Typography>
  );
}

// type 별 부가요소 — 텍스트(content) 아래에 렌더된다.
function DescriptionExtra({
  description,
}: {
  description: TaskEditorDescription;
}) {
  switch (description.type) {
    case "basic":
      return null;

    case "image": {
      if (!description.image_url) {
        return (
          <Sheet
            sx={{
              border: "1px dashed",
              borderColor: theme.vars.palette.Border.Weak,
              backgroundColor: theme.vars.palette.Background.Level1,
              borderRadius: "6px",
              p: "16px",
              textAlign: "center",
            }}
          >
            <Typography
              variant="Caption"
              sx={{ color: theme.vars.palette.Content.Weak }}
            >
              (이미지 없음)
            </Typography>
          </Sheet>
        );
      }
      return (
        <Box
          component="img"
          src={description.image_url}
          alt=""
          sx={{ width: "100%", borderRadius: "6px", display: "block" }}
        />
      );
    }

    case "label": {
      // 레이블-텍스트 쌍을 세로로 나열 — 텍스트가 비어있으면 레이블만 렌더.
      return (
        <Stack spacing="8px">
          {description.pairs.map((pair, pairIndex) => {
            const isEmpty = !pair.label;
            return (
              <Stack key={pairIndex} spacing="2px">
                <Typography
                  variant="Caption"
                  sx={{
                    color: isEmpty
                      ? theme.vars.palette.Content.Weak
                      : theme.vars.palette.Content.Highlight,
                    fontWeight: 700,
                  }}
                >
                  {pair.label || "(레이블 없음)"}
                </Typography>
                {pair.text ? (
                  <Typography
                    variant="Body"
                    sx={{
                      color: theme.vars.palette.Content.Strong,
                      fontWeight: 500,
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.5,
                    }}
                  >
                    {pair.text}
                  </Typography>
                ) : null}
              </Stack>
            );
          })}
        </Stack>
      );
    }

    case "reference": {
      if (description.reference_type === "coach_preset") {
        const text = description.preset_text;
        if (!text) {
          return (
            <Typography
              variant="Body"
              sx={{ color: theme.vars.palette.Content.Weak, fontWeight: 500 }}
            >
              (코치 프리셋 비어있음)
            </Typography>
          );
        }
        return (
          <Typography
            variant="Body"
            sx={{
              color: theme.vars.palette.Content.Strong,
              fontWeight: 500,
              whiteSpace: "pre-wrap",
              lineHeight: 1.5,
            }}
          >
            {text}
          </Typography>
        );
      }
      return (
        <Typography
          variant="Body"
          sx={{
            color: theme.vars.palette.Content.Strong,
            fontWeight: 500,
            whiteSpace: "pre-wrap",
            lineHeight: 1.5,
          }}
        >
          {PREVIEW_SAMPLE_AUTOMATIC_THOUGHT}
        </Typography>
      );
    }

    default: {
      const _never: never = description;
      void _never;
      return null;
    }
  }
}

function PreviewDescription({
  description,
  blockType,
}: PreviewDescriptionProps) {
  return (
    <Stack spacing="12px">
      <ContentText content={description.content} blockType={blockType} />
      <DescriptionExtra description={description} />
    </Stack>
  );
}

export default PreviewDescription;
